import React, {createContext, useContext} from 'react';

import {
  type GraphAppearance,
  type GraphAxis,
  type GraphRendering,
} from './graphRenderProps';

/** Internal wiring projected from public renderer facts, with no application settings. */
export type GraphRendererSettings = {
  xAxis: GraphAxis['xKey'];
  xAxisIsDatetime: GraphAxis['xIsDatetime'];
  xLogScale: boolean;
  yLogScale: boolean;
  pointVisualizationMethod: GraphRendering['pointStrategy'];
  uplotEnabled: boolean;
} & Pick<GraphAppearance, 'isDarkMode'> &
  Pick<GraphRendering, 'forceReactVis' | 'isFullFidelity' | 'plotType'> & {
    isMultiMetric: boolean;
  };

const LinePlotSettingsContext = createContext<GraphRendererSettings | null>(
  null
);

/**
 * GraphWrapper's provider. The value must keep its identity while its fields
 * are unchanged.
 */
export function LinePlotSettingsProvider({
  children,
  settings,
}: {
  children: React.ReactNode;
  settings: GraphRendererSettings;
}) {
  return (
    <LinePlotSettingsContext.Provider value={settings}>
      {children}
    </LinePlotSettingsContext.Provider>
  );
}

export function useLinePlotSettings(): GraphRendererSettings {
  const settings = useContext(LinePlotSettingsContext);
  if (settings == null) {
    throw new Error(
      'useLinePlotSettings must be called inside Graph, which provides the line plot settings'
    );
  }
  return settings;
}
